import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import CMSService from '../../services/CMSService';
import { HeaderConfig } from '../../types';
import RegisterForm from './RegisterForm';

// Primary color from design system
const PRIMARY_COLOR = '#017e6e';

// Fallback header data when API fails
const DEFAULT_HEADER_DATA: HeaderConfig = {
  _id: 'fallback-header',
  logo: {
    url: '',
    linkTo: '/',
  },
  navigation: [
    {
      id: 'nav-1',
      label: 'Khóa học',
      url: '/courses',
      submenu: [
        {
          catalogName: 'IELTS',
          catalogItems: [
            { label: 'IELTS Preparation Pro', url: '/courses/ielts-prep' },
            { label: 'IELTS Band 8+', url: '/courses/ielts-band8' },
            { label: 'IELTS Intensive', url: '/courses/ielts-intensive' },
          ],
        },
        {
          catalogName: 'Kỹ năng',
          catalogItems: [
            { label: 'Speaking Mastery', url: '/courses/speaking' },
            { label: 'Writing Excellence', url: '/courses/writing' },
            { label: 'Listening Expert', url: '/courses/listening' },
            { label: 'Reading Advanced', url: '/courses/reading' },
          ],
        },
      ],
    },
    {
      id: 'nav-2',
      label: 'Bài tập',
      url: '/exercises',
      submenu: [
        {
          catalogName: 'Theo kỹ năng',
          catalogItems: [
            { label: 'Bài tập Reading', url: '/exercises/reading' },
            { label: 'Bài tập Writing', url: '/exercises/writing' },
            { label: 'Bài tập Listening', url: '/exercises/listening' },
            { label: 'Bài tập Speaking', url: '/exercises/speaking' },
          ],
        },
      ],
    },
    {
      id: 'nav-3',
      label: 'Blog',
      url: '/blogs',
    },
    {
      id: 'nav-4',
      label: 'Về chúng tôi',
      url: '/about',
    },
    {
      id: 'nav-5',
      label: 'Liên hệ',
      url: '/contact',
    },
  ],
  ctaButtons: {
    login: {
      label: 'Đăng nhập',
      url: '/login',
    },
    register: {
      label: 'Đăng ký',
      url: '/register',
    },
  },
  updatedAt: new Date(),
};

/**
 * Navigation item with optional dropdown
 */
interface NavigationItem {
  id: string;
  label: string;
  href: string;
  hasDropdown: boolean;
  dropdownContent?: {
    columns: number;
    sections: Array<{
      title: string;
      links: Array<{
        label: string;
        href: string;
      }>;
    }>;
  };
}

/**
 * Map API data to the format expected by the components
 */
function mapNavigationData(apiNavigation: any[]): NavigationItem[] {
  if (!apiNavigation || !Array.isArray(apiNavigation)) return [];

  return apiNavigation.map((item) => {
    const hasDropdown = item.submenu && item.submenu.length > 0;

    const navItem: NavigationItem = {
      id: item.id || item.label,
      label: item.label,
      href: item.url,
      hasDropdown,
    };

    if (hasDropdown) {
      navItem.dropdownContent = {
        columns: item.submenu.length,
        sections: item.submenu.map((submenuItem: any) => ({
          title: submenuItem.catalogName, // Category/Catalog name as section title
          links: submenuItem.catalogItems.map((subItem: any) => ({
            label: subItem.label,
            href: subItem.url,
          })),
        })),
      };
    }

    return navItem;
  });
}

/**
 * Header Component for Landing Page
 * Navigation header with logo, menu, submenus/categories, and CTA buttons
 * Matching original design with teal (#017e6e) primary color and glass-morphism effect
 */
const Header: React.FC = () => {
  const [headerData, setHeaderData] = useState<HeaderConfig>(DEFAULT_HEADER_DATA);
  const [navigationData, setNavigationData] = useState<NavigationItem[]>([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedMenu, setExpandedMenu] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showRegisterModal, setShowRegisterModal] = useState(false);

  useEffect(() => {
    const loadHeaderData = async () => {
      try {
        const response = await CMSService.getHeaderConfig();
        if (response?.statusCode === 200 && response?.data) {
          setHeaderData(response.data);
          const mapped = mapNavigationData(response.data.navigation);
          setNavigationData(mapped);
        } else {
          // API failed, use fallback data
          const mapped = mapNavigationData(DEFAULT_HEADER_DATA.navigation);
          setNavigationData(mapped);
        }
      } catch (error) {
        // API error, use fallback data
        console.warn('Failed to load header config, using fallback data:', error);
        const mapped = mapNavigationData(DEFAULT_HEADER_DATA.navigation);
        setNavigationData(mapped);
      } finally {
        setLoading(false);
      }
    };

    loadHeaderData();
  }, []);

  // Get CTA buttons data with fallback
  const loginButton = headerData?.ctaButtons?.login || DEFAULT_HEADER_DATA.ctaButtons?.login || {
    label: 'Đăng nhập',
    url: '/login',
  };

  const registerButton = headerData?.ctaButtons?.register || DEFAULT_HEADER_DATA.ctaButtons?.register || {
    label: 'Đăng ký',
    url: '/register',
  };

  const logoData = headerData?.logo;

  // Inline styles for underline effect
  const underlineStyle = `
    .nav-link::after {
      content: '';
      position: absolute;
      left: 0;
      bottom: -2px;
      width: 0;
      height: 2px;
      background-color: ${PRIMARY_COLOR};
      transition: width 0.2s ease;
    }
    
    .nav-link:hover::after {
      width: 100%;
    }
    
    .dropdown-link::after {
      content: '';
      position: absolute;
      left: 0;
      bottom: 0;
      width: 0;
      height: 2px;
      background-color: ${PRIMARY_COLOR};
      transition: width 0.2s ease;
    }
    
    .dropdown-link:hover::after {
      width: 100%;
    }
  `;

  return (
    <>
      <style>{underlineStyle}</style>

      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-sm shadow-sm border-b border-gray-100/80">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link
              to="/"
              className="flex items-center gap-2 hover:opacity-80 transition-opacity flex-shrink-0"
            >
              {logoData?.url && (
                <img
                  src={logoData.url}
                  alt="Logo"
                  className="h-20 w-20 object-contain"
                />
              )}
              <div className="flex items-center gap-1">
                <span className="text-sm font-semibold text-gray-800">ANH NGỮ</span>
                <span className="text-sm font-semibold" style={{ color: '#D32F2F' }}>
                  JENNIFER
                </span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden xl:flex items-center gap-1">
              {navigationData.length > 0 ? (
                navigationData.map((nav) => (
                  <div key={nav.id} className="relative group">
                    {nav.hasDropdown ? (
                      <button
                        className="px-4 py-2 rounded-md relative text-gray-700 hover:text-gray-900 transition-colors duration-200 flex items-center gap-2"
                      >
                        <span>{nav.label}</span>
                        <i className="fas fa-chevron-down text-xs"></i>
                      </button>
                    ) : (
                      <Link
                        to={nav.href}
                        className="px-4 py-2 rounded-md relative text-gray-700 hover:text-gray-900 transition-colors duration-200 nav-link inline-block"
                      >
                        {nav.label}
                      </Link>
                    )}

                    {/* Dropdown Menu with Categories */}
                    {nav.hasDropdown && nav.dropdownContent && (
                      <div
                        className="absolute left-0 mt-0 w-max bg-white shadow-lg rounded-md border border-gray-200 z-50 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 p-4"
                        style={{
                          display: 'grid',
                          gridTemplateColumns: `repeat(${nav.dropdownContent.columns}, minmax(200px, 1fr))`,
                          gap: '1.5rem',
                        }}
                      >
                        {nav.dropdownContent.sections.map((section, idx) => (
                          <div key={idx} className="space-y-2">
                            {/* Category Title */}
                            <h4 className="text-gray-800 font-medium text-sm px-3">
                              {section.title}
                            </h4>
                            {/* Category Links */}
                            <ul className="space-y-0">
                              {section.links.map((link, linkIdx) => (
                                <li key={linkIdx}>
                                  <Link
                                    to={link.href}
                                    className="block relative px-3 py-2 rounded-md text-sm text-gray-700 hover:bg-gray-50 dropdown-link transition-colors duration-200"
                                  >
                                    {link.label}
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <>
                  <button className="px-4 py-2 rounded-md relative text-gray-700 hover:text-gray-900 nav-link transition-colors">
                    Khóa học
                  </button>
                  <button className="px-4 py-2 rounded-md relative text-gray-700 hover:text-gray-900 nav-link transition-colors">
                    Blog
                  </button>
                  <button className="px-4 py-2 rounded-md relative text-gray-700 hover:text-gray-900 nav-link transition-colors">
                    Về chúng tôi
                  </button>
                </>
              )}
            </div>

            {/* CTA Buttons */}
            <div className="hidden xl:flex items-center gap-3 flex-shrink-0">
              {/* Register Button - with wave effect and red background */}
              <button
                onClick={() => setShowRegisterModal(true)}
                className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 overflow-hidden rounded-md group hover:bg-red-700 transition-colors cursor-pointer"
              >
                {/* Wave ripple on click */}
                <span className="absolute inset-0 overflow-hidden rounded-md">
                  <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-active:animate-wave"></span>
                </span>
                
                {/* Content */}
                <span className="relative flex items-center z-10">
                  {'Đăng ký'}
                  <i className="fas fa-user-plus ml-2 text-sm"></i>
                </span>
              </button>
            </div>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="xl:hidden text-gray-700 text-2xl transition-colors"
            >
              <i className={`fas ${mobileMenuOpen ? 'fa-times' : 'fa-bars'}`}></i>
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="xl:hidden mt-4 space-y-2 pb-4 border-t border-gray-100 pt-4">
              {navigationData.length > 0 ? (
                navigationData.map((nav) => (
                  <div key={nav.id}>
                    {nav.hasDropdown ? (
                      <button
                        onClick={() =>
                          setExpandedMenu(expandedMenu === nav.id ? null : nav.id)
                        }
                        className="w-full text-left px-4 py-3 rounded-md bg-gray-50 hover:bg-gray-100 transition-colors font-medium text-gray-900 flex justify-between items-center"
                      >
                        <span>{nav.label}</span>
                        <i
                          className={`fas fa-chevron-down transition-transform text-xs ${
                            expandedMenu === nav.id ? 'rotate-180' : ''
                          }`}
                        ></i>
                      </button>
                    ) : (
                      <Link
                        to={nav.href}
                        className="block w-full text-left px-4 py-3 rounded-md bg-gray-50 hover:bg-gray-100 transition-colors font-medium text-gray-900"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {nav.label}
                      </Link>
                    )}

                    {/* Mobile Dropdown */}
                    {nav.hasDropdown &&
                      expandedMenu === nav.id &&
                      nav.dropdownContent && (
                        <div className="pl-4 mt-2 space-y-3">
                          {nav.dropdownContent.sections.map((section, idx) => (
                            <div key={idx}>
                              <h5 className="font-medium text-gray-800 text-sm px-3 mb-2">
                                {section.title}
                              </h5>
                              <ul className="space-y-0 ml-2">
                                {section.links.map((link, linkIdx) => (
                                  <li key={linkIdx}>
                                    <Link
                                      to={link.href}
                                      className="block px-3 py-2 rounded-md text-gray-700 hover:bg-gray-50 text-sm transition-colors duration-200"
                                      onClick={() => setMobileMenuOpen(false)}
                                    >
                                      {link.label}
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      )}
                  </div>
                ))
              ) : (
                <>
                  <button className="w-full text-left px-4 py-3 rounded-md bg-gray-50 hover:bg-gray-100 transition-colors font-medium text-gray-900">
                    Khóa học
                  </button>
                  <button className="w-full text-left px-4 py-3 rounded-md bg-gray-50 hover:bg-gray-100 transition-colors font-medium text-gray-900">
                    Blog
                  </button>
                  <button className="w-full text-left px-4 py-3 rounded-md bg-gray-50 hover:bg-gray-100 transition-colors font-medium text-gray-900">
                    Về chúng tôi
                  </button>
                </>
              )}

              {/* Mobile CTA Buttons */}
              <div className="border-t border-gray-100 pt-3 mt-3 space-y-2">
                <Link
                  to={loginButton?.url || '/login'}
                  className="block w-full px-4 py-3 text-center text-gray-700 border-2 border-gray-300 rounded-md hover:bg-gray-50 transition-colors font-medium text-sm"
                >
                  {loginButton?.label || 'Đăng nhập'}
                </Link>
                <button
                  onClick={() => {
                    setShowRegisterModal(true);
                    setMobileMenuOpen(false);
                  }}
                  className="block w-full px-4 py-3 text-center text-white rounded-md hover:opacity-90 transition-opacity font-medium text-sm cursor-pointer"
                  style={{ backgroundColor: PRIMARY_COLOR }}
                >
                  {registerButton?.label || 'Đăng ký'}
                </button>
              </div>
            </div>
          )}
        </nav>
      </header>

      {/* Register Modal */}
      {showRegisterModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="relative w-full max-w-md">
            {/* Close Button */}
            <button
              onClick={() => setShowRegisterModal(false)}
              className="absolute -top-10 right-0 text-white hover:text-gray-200 text-3xl transition-colors"
            >
              <i className="fas fa-times"></i>
            </button>
            
            {/* Modal Content */}
            <RegisterForm />
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
